import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { monsterKind } from '../../utils/monsterKind';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-2675" data-mp="1geziqw0ji03p">
      <EventPositionList w={w} positions={list} max={12} data-id="element-2676" data-mp="1le2mmd4sa0a3" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds, monsterPath } from '../../utils/worldFormat';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { monsterIdFromPrettyPath } from '../../utils/prettyRoutes';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2677" data-mp="1apzlra5d017r">
      {w => {
      const id = slug ? monsterIdFromPrettyPath(pathname) : Number(monsterId);
      const m = id === undefined ? undefined : w.monsters.get(id);
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2678" data-mp="i0iuwgxe0sdn">
              <p className="text-white" data-id="element-2679" data-mp="e2bddjv3nn1e">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2680" data-mp="rtgi30dbr5il">Volver a Monstruos</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={monsterPath(m.id)} data-id="element-2681" data-mp="3vggk3zmdxuad" />
            <MonsterView w={w} m={m} data-id="element-2682" data-mp="3vf5hw48l8uld" />
          </>;
    }}
    </WorldGate>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-2683" data-mp="14ahizmikcobb">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc || monsterKind(w, m) === 'npc' ? 'NPC' : 'Monstruo'} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-2685" data-mp="1jqxnqusybeu1" /> : undefined} data-id="element-2684" data-mp="14ar8za3lyvh0">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2686" data-mp="1atn9d0ku79kw">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2687" data-mp="27i33zb2t094v">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2688" data-mp="1ygn6g3x381i2" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2689" data-mp="1thscq6xl0sj6">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-2690" data-mp="1tky1fl2atsed">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-2691" data-mp="i2ddewhwhokw" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-2692" data-mp="812sw03n85l9">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2693" data-mp="czck5zm8ym1l" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-2694" data-mp="mq78ramytxq7">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-2695" data-mp="10nvwcq8f3iyy">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2696" data-mp="10oymux1tvjec">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-2697" data-mp="15n02t93yj2ll">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-2698" data-mp="32hcyxfhoxtmu">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2699" data-mp="37fo8rq7zk452">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-2700" data-mp="39cwd859fvb2b" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-2701" data-mp="3ib8a2p0wf537">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-2702" data-mp="3dbvo06huq21o">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2703" data-mp="3s34dmflr7net" />)}</ul> : <p className="text-gray-400" data-id="element-2704" data-mp="3n41jhq7bb1lm">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-2705" data-mp="4yya3tabc3fe" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-2706" data-mp="xuriz0vrtah">
            <p className="mb-3 text-sm text-gray-400" data-id="element-2707" data-mp="jldexu8krl9r">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-2708" data-mp="ak5sqajc2aw1">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2709" data-mp="ajnnioep0mbz" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-2710" data-mp="fl370c182ks9">
            <div className="flex flex-wrap gap-2" data-id="element-2711" data-mp="s9i60028sax">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2712" data-mp="5m4afrtqeflm" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-2713" data-mp="3nzv49z4dw3gi" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-2714" data-mp="3nx8vjw5rbhwp" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-2715" data-mp="3j1tiv7jhjs1v">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-2716" data-mp="2bb5oqucmhcr" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2717" data-mp="31src8q8aswyf">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>
      </div>
    </div>;
}